import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";

import { api, ensureCsrf, getActiveOrganization, setActiveOrganization } from "../api/client";
import { configureFormatting } from "../i18n/format";
import type { components } from "../api/schema";

export type Me = components["schemas"]["Me"];
export type LoginResult = { mfaRequired: true; mfaToken: string } | { mfaRequired: false; me: Me };
export type OrganizationSummary = components["schemas"]["OrganizationSummary"];

type AuthContextValue = {
  me: Me | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (mfaToken: string, code: string) => Promise<Me>;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  switchOrganization: (slug: string) => void;
  hasPermission: (code: string) => boolean;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const meQuery = useQuery({
    queryKey: ["me", getActiveOrganization()],
    queryFn: async () => {
      const { data, response } = await api.GET("/api/v1/accounts/me/");
      if (response.status === 401 || response.status === 403) return null;
      if (data) configureFormatting({ locale: data.locale || data.active_organization?.locale, timezone: data.timezone || data.active_organization?.timezone });
      return data ?? null;
    },
    retry: false,
  });

  const adopt = useCallback(
    async (me: Me) => {
      const preferred = me.active_organization?.slug ?? me.organizations[0]?.slug ?? null;
      setActiveOrganization(preferred);
      queryClient.setQueryData(["me", preferred], me);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    [queryClient],
  );

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      await ensureCsrf();
      const { data, error } = await api.POST("/api/v1/accounts/auth/login/", { body: { email, password } });
      if (error || !data) throw error ?? new Error("Login failed");
      if (data.mfa_required) return { mfaRequired: true, mfaToken: data.mfa_token ?? "" };
      await adopt(data.me!);
      return { mfaRequired: false, me: data.me! };
    },
    [adopt],
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, code: string) => {
      const { data, error } = await api.POST("/api/v1/accounts/auth/mfa/verify/", { body: { mfa_token: mfaToken, code } });
      if (error || !data?.me) throw error ?? new Error("Verification failed");
      await adopt(data.me);
      return data.me;
    },
    [adopt],
  );

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["me"] });
  }, [queryClient]);

  const logout = useCallback(async () => {
    await api.POST("/api/v1/accounts/auth/logout/");
    setActiveOrganization(null);
    queryClient.clear();
  }, [queryClient]);

  const switchOrganization = useCallback(
    (slug: string) => {
      setActiveOrganization(slug);
      queryClient.clear();
      void api.POST("/api/v1/accounts/members/switch/");
      void meQuery.refetch();
    },
    [queryClient, meQuery],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      me: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      login,
      verifyMfa,
      refresh,
      logout,
      switchOrganization,
      hasPermission: (code) => !!meQuery.data?.permissions.includes(code),
    }),
    [meQuery.data, meQuery.isLoading, login, verifyMfa, refresh, logout, switchOrganization],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
